import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BrandMark } from "../components/Brand";
import { Input } from "../components/ui/input";
import { api, formatCurrency, formatDate, formatApiError, openAuthedPdf } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { getAttorney, attorneyLogout, saveAttorney } from "../lib/attorneyAuth";
import { STATUS_STYLES } from "../components/ops/opsUtils";
import ImpersonationBanner from "../components/ImpersonationBanner";
import {
  LogOut, Search, FileSignature, Receipt, ArrowUpRight, Building2,
  TrendingUp, FolderOpen, Loader2, AlertCircle, ChevronRight, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

// Original advance + additional advances (historical dollars advanced on this loan)
const principalAdvanced = (l) => (l.principal || 0) + (l.additional_advances || []).reduce((a, x) => a + (Number(x.amount) || 0), 0);

export default function AttorneyDashboard() {
  useSEO("Firm portal — Instabridge", "Real-time balances, payoffs, and documents for your firm's cases.");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [me, setMe] = useState(null);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("active");
  const [generating, setGenerating] = useState(null);
  const welcome = params.get("welcome");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const meRes = await api.get("/portal/me");
        if (cancelled) return;
        setMe(meRes.data);
        // refresh persisted blob so other pages know the firm AND impersonation state
        const prev = getAttorney() || {};
        saveAttorney({
          ...prev,
          user: { ...meRes.data.user, role: "attorney" },
          firm: meRes.data.firm,
          impersonation: meRes.data.impersonation || null,
        });
        const ln = await api.get("/portal/loans");
        if (!cancelled) setLoans(ln.data);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          try { localStorage.removeItem("instabridge_attorney"); } catch (err) { console.error("clear-attorney-state", err); }
          if (!cancelled) navigate("/attorney-login", { replace: true });
          return;
        }
        if (status === 403) {
          if (!cancelled) setAuthError(formatApiError(e));
          return;
        }
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // Group multi-advance clients into a single dashboard row. Each row is
  // either a single loan ({type:'loan', loan}) or a client group
  // ({type:'client', client_name, advances:[...], totals}).
  const filteredGroups = useMemo(() => {
    const ACTIVE = new Set(["Funded", "Active", "PendingSettlement", "PayoffRequested"]);
    const filtered = loans.filter((l) => {
      if (filter === "active" && !ACTIVE.has(l.current_status)) return false;
      if (filter === "settled" && l.current_status !== "Settled") return false;
      if (search && !(l.client_name || "").toLowerCase().includes(search.toLowerCase())
          && !(l.loan_id || "").toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
    const groupKey = (l) => l.client_group_id || `name:${(l.client_name || l.loan_id || "").toLowerCase()}`;
    const byClient = new Map();
    for (const l of filtered) {
      const k = groupKey(l);
      const list = byClient.get(k) || [];
      list.push(l);
      byClient.set(k, list);
    }
    const out = [];
    // Preserve original sort order: by the first appearance of each group in `filtered`
    const seen = new Set();
    for (const l of filtered) {
      const k = groupKey(l);
      if (seen.has(k)) continue;
      seen.add(k);
      const advances = byClient.get(k);
      if (advances.length === 1) {
        out.push({ type: "loan", loan: advances[0] });
      } else {
        // Most-recent status wins; sum principal/payoff
        const totalPrincipal = advances.reduce((s, x) => s + principalAdvanced(x), 0);
        const totalPayoff = advances.reduce((s, x) => s + (x.current_payoff || 0), 0);
        const latest = advances.reduce((a, b) => (a.funding_date > b.funding_date ? a : b));
        out.push({
          type: "client",
          client_name: latest.client_name,
          client_key: latest.client_group_id || (latest.client_name || "").trim().toLowerCase().replace(/\s+/g, "-"),
          advance_count: advances.length,
          total_principal: totalPrincipal,
          total_payoff: totalPayoff,
          latest_status: latest.current_status,
          first_funded: advances.reduce((a, b) => (a.funding_date < b.funding_date ? a : b)).funding_date,
          case_type: latest.case_type,
        });
      }
    }
    return out;
  }, [loans, filter, search]);

  const filtered = filteredGroups; // alias kept for compatibility with old footer count

  const logout = async () => { await attorneyLogout(); navigate("/attorney-login"); };

  const quickPayoff = async (loanId) => {
    setGenerating(loanId);
    try {
      const { data } = await api.post(`/portal/loans/${loanId}/quick-payoff`, { valid_days: 10 });
      toast.success(`Payoff ${data.statement_number} generated`);
      await openAuthedPdf(`/portal/payoffs/${data.payoff_id}/pdf`, `${data.statement_number || data.payoff_id}.pdf`);
      // refresh loans (status flipped to PayoffRequested)
      const ln = await api.get("/portal/loans");
      setLoans(ln.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(null);
    }
  };

  if (loading) return <Splash />;

  if (authError) return <NotInFirm message={authError} onLogout={logout} />;
  if (!me) return null;

  const s = me.summary;

  return (
    <div className="min-h-screen bg-[#F7F9FC]" data-testid="attorney-dashboard">
      <ImpersonationBanner impersonation={me.impersonation} firm={me.firm} />
      {/* Topbar */}
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-30">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8 h-14 flex items-center justify-between gap-4">
          <Link to="/attorney/portal"><BrandMark /></Link>
          <div className="flex items-center gap-4">
            <Link to="/attorney/portal/documents" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[#374151] hover:text-[#0066FF]" data-testid="nav-documents">
              <FolderOpen className="w-4 h-4" /> Documents
            </Link>
            <div className="hidden md:flex flex-col items-end leading-tight">
              <span className="text-xs text-[#0A2540] font-medium">{me.user.name || me.user.email}</span>
              <span className="text-[10px] uppercase tracking-wider text-[#6B7280]">{me.firm.name}</span>
            </div>
            <button onClick={logout} className="text-xs text-[#6B7280] hover:text-[#0A2540] inline-flex items-center gap-1" data-testid="attorney-logout-btn"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-5 lg:px-8 py-7">
        {/* Welcome */}
        {welcome && (
          <div className="mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8] text-xs font-medium" data-testid="welcome-pill">
            <Sparkles className="w-3.5 h-3.5" /> Welcome back to {welcome}
          </div>
        )}

        {/* Hero */}
        <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-1">
              <Building2 className="w-3.5 h-3.5" /> Firm portal
            </div>
            <h1 className="font-display text-4xl lg:text-5xl text-[#0A2540] font-semibold tracking-tight">{me.firm.name}</h1>
            <p className="text-sm text-[#6B7280] mt-1.5">
              Real-time balances, instant payoffs, and your firm's case history — all in one place.
            </p>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-7">
          <Kpi label="Active cases" value={s.active_count} accent testid="kpi-active" />
          <Kpi label="Outstanding payoff" value={formatCurrency(s.total_outstanding_payoff)} hot testid="kpi-payoff" />
          <Kpi label="Total funded all-time" value={formatCurrency(s.total_funded_all_time)} testid="kpi-funded" />
          <Kpi label="Accrued interest" value={formatCurrency(s.total_accrued_interest)} testid="kpi-accrued" />
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E7EB] flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by client or loan ID…" className="pl-9 h-9 border-[#E5E7EB]" data-testid="portal-search" />
            </div>
            <div className="flex items-center gap-1 p-1 rounded-lg bg-[#F3F4F6]">
              {[
                { v: "active", l: "Active" },
                { v: "settled", l: "Settled" },
                { v: "all", l: "All" },
              ].map((t) => (
                <button key={t.v} onClick={() => setFilter(t.v)} data-testid={`filter-${t.v}`}
                  className={`px-3 h-7 rounded-md text-xs font-medium transition-colors ${
                    filter === t.v ? "bg-white text-[#0A2540] shadow-sm" : "text-[#6B7280] hover:text-[#0A2540]"
                  }`}>{t.l}</button>
              ))}
            </div>
            <div className="ml-auto text-xs text-[#6B7280] tabular-nums">{filtered.length} of {loans.length}</div>
          </div>

          {/* Loans table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280] bg-[#F9FAFB]">
                  <th className="px-4 py-2.5 font-medium">Client</th>
                  <th className="px-4 py-2.5 font-medium">Loan</th>
                  <th className="px-4 py-2.5 font-medium">Funded</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium text-right">Principal advanced</th>
                  <th className="px-4 py-2.5 font-medium text-right">Current payoff</th>
                  <th className="px-4 py-2.5 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-16 text-center" data-testid="portal-empty">
                    <div className="inline-flex flex-col items-center gap-2 text-[#6B7280]">
                      <FileSignature className="w-6 h-6 text-[#9CA3AF]" />
                      <div className="text-sm">No cases match your filters.</div>
                    </div>
                  </td></tr>
                ) : filtered.map((row) => {
                  // Client group row (multi-advance) vs single loan row
                  if (row.type === "client") {
                    const status = STATUS_STYLES[row.latest_status] || { bg: "#F3F4F6", text: "#374151", label: row.latest_status };
                    return (
                      <tr key={`c-${row.client_key}`} className="border-t border-[#F3F4F6] hover:bg-[#F9FAFB] group" data-testid={`client-row-${row.client_key}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Link to={`/attorney/portal/clients/${row.client_key}`} className="font-medium text-[#0A2540] hover:text-[#0066FF]" data-testid={`client-link-${row.client_key}`}>
                              {row.client_name}
                            </Link>
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-[#EFF6FF] text-[#1D4ED8]" data-testid={`advances-badge-${row.client_key}`}>{row.advance_count} advances</span>
                          </div>
                          {row.case_type && <div className="text-[11px] text-[#6B7280] mt-0.5">{row.case_type}</div>}
                        </td>
                        <td className="px-4 py-3 text-xs text-[#6B7280] italic">multiple</td>
                        <td className="px-4 py-3 text-xs text-[#6B7280] tabular-nums">since {formatDate(row.first_funded)}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ backgroundColor: status.bg, color: status.text }}>{status.label}</span>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-[#0A2540]">{formatCurrency(row.total_principal)}</td>
                        <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#0066FF]">{formatCurrency(row.total_payoff)}</td>
                        <td className="px-4 py-3 text-right">
                          <Link to={`/attorney/portal/clients/${row.client_key}`} className="inline-flex items-center justify-center h-8 w-8 rounded-md text-[#6B7280] hover:text-[#0A2540] hover:bg-[#F3F4F6]" data-testid={`view-client-${row.client_key}`}>
                            <ChevronRight className="w-4 h-4" />
                          </Link>
                        </td>
                      </tr>
                    );
                  }
                  const l = row.loan;
                  const status = STATUS_STYLES[l.current_status] || { bg: "#F3F4F6", text: "#374151", label: l.current_status };
                  const isClosed = l.current_status === "Settled" || l.current_status === "Closed" || l.current_status === "Declined";
                  return (
                    <tr key={l.loan_id} className="border-t border-[#F3F4F6] hover:bg-[#F9FAFB] group" data-testid={`loan-row-${l.loan_id}`}>
                      <td className="px-4 py-3">
                        <Link to={`/attorney/portal/loans/${l.loan_id}`} className="font-medium text-[#0A2540] hover:text-[#0066FF]" data-testid={`loan-client-${l.loan_id}`}>
                          {l.client_name}
                        </Link>
                        {l.case_type && <div className="text-[11px] text-[#6B7280] mt-0.5">{l.case_type}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] tabular-nums font-mono">{l.loan_id}</td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] tabular-nums">{formatDate(l.funding_date)}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ backgroundColor: status.bg, color: status.text }}>{status.label}</span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-[#0A2540]">{formatCurrency(principalAdvanced(l))}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#0066FF]">{formatCurrency(l.current_payoff)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {!isClosed && (
                            <button
                              onClick={() => quickPayoff(l.loan_id)}
                              disabled={generating === l.loan_id}
                              data-testid={`quick-payoff-${l.loan_id}`}
                              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-[#0A2540] hover:bg-[#001A3D] text-white text-xs font-medium transition-colors disabled:opacity-60"
                            >
                              {generating === l.loan_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Receipt className="w-3.5 h-3.5" />}
                              Payoff
                            </button>
                          )}
                          <Link to={`/attorney/portal/loans/${l.loan_id}`} className="inline-flex items-center justify-center h-8 w-8 rounded-md text-[#6B7280] hover:text-[#0A2540] hover:bg-[#F3F4F6]" data-testid={`view-${l.loan_id}`}>
                            <ChevronRight className="w-4 h-4" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick links row */}
        <div className="grid sm:grid-cols-2 gap-3 mt-6">
          <Link to="/attorney/portal/documents" className="flex items-center justify-between p-4 rounded-xl bg-white border border-[#E5E7EB] hover:border-[#0066FF] hover:shadow-sm transition-all group" data-testid="link-vault">
            <div>
              <div className="flex items-center gap-2 text-[#0A2540] font-medium"><FolderOpen className="w-4 h-4 text-[#0066FF]" /> Document vault</div>
              <div className="text-xs text-[#6B7280] mt-0.5">All agreements, payoff letters, and reductions across {me.firm.name}.</div>
            </div>
            <ArrowUpRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#0066FF]" />
          </Link>
          <div className="flex items-center justify-between p-4 rounded-xl bg-[#0A2540] text-white border border-[#0A2540]" data-testid="card-summary-mail">
            <div>
              <div className="flex items-center gap-2 font-medium"><TrendingUp className="w-4 h-4 text-[#7DD3FC]" /> Need help or balances?</div>
              <div className="text-xs text-white/70 mt-0.5">Reach Instabridge ops at <a className="underline" href="mailto:payoffs@instabridgefunding.com">payoffs@instabridgefunding.com</a></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent, hot, testid }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5">{label}</div>
      <div className={`tabular-nums font-display text-2xl lg:text-3xl font-semibold ${hot ? "text-[#B91C1C]" : accent ? "text-[#0066FF]" : "text-[#0A2540]"}`}>{value}</div>
    </div>
  );
}

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center text-[#6B7280]" data-testid="portal-loading">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading your portal…
    </div>
  );
}

function NotInFirm({ message, onLogout }) {
  return (
    <div className="min-h-screen bg-[#F7F9FC] flex items-center justify-center px-5" data-testid="portal-not-in-firm">
      <div className="max-w-md w-full bg-white border border-[#E5E7EB] rounded-2xl p-8 text-center">
        <div className="w-14 h-14 rounded-2xl bg-[#FEF3C7] mx-auto flex items-center justify-center mb-4">
          <AlertCircle className="w-6 h-6 text-[#D97706]" />
        </div>
        <h1 className="font-display text-2xl text-[#0A2540] font-semibold mb-2">Almost there</h1>
        <p className="text-sm text-[#6B7280] mb-6">{message}</p>
        <button onClick={onLogout} className="text-xs text-[#6B7280] hover:text-[#0A2540]">Sign out</button>
      </div>
    </div>
  );
}
